import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useCurrentTime } from "@/hooks/use-current-time";
import { useLocations } from "@/hooks/use-locations";
import { useModule } from "@/hooks/use-module";
import { getClockedInDurationMs } from "@/lib/check-in-duration-warning";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toMs } from "@/lib/timestamp";
import { EventType } from "@/types/enums";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
import { clockInQueryOptions, useClockInMutation, useClockOutMutation } from "@/query/check-in/clock-in";
export function useClockIn() {
  const hasClockInModule = useModule("CLOCK_IN");
  const {
    currentUser
  } = useUser();
  const {
    locations,
    isLocationsLoading
  } = useLocations();
  const {
    data: timeEntry,
    isPending: isClockInPending,
    error: getClockInError
  } = useQuery({
    ...clockInQueryOptions.current(),
    enabled: hasClockInModule && !hasPermission(currentUser, PERMISSIONS.CLOCK_IN_SKIP),
    staleTime: 15 * 60 * 1000,
    // 15 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });
  const currentTime = useCurrentTime(5 * 60 * 1000); // 5 minutes

  // Today on the clinic's clock, re-derived as the current time ticks so the
  // window rolls over at clinic midnight rather than staying on mount day.
  const clock = useClinicClock();
  const today = clock.dayOf(currentTime);
  const {
    start: dayStart,
    end: dayEnd
  } = useMemo(() => clock.dayRange(today), [clock, today]);
  const {
    data: todayShifts
  } = useQuery({
    ...eventQueryOptions.list(dayStart, dayEnd, [currentUser?.id || ""], undefined, [EventType.SHIFT]),
    enabled: hasClockInModule && !!currentUser?.id && !hasPermission(currentUser, PERMISSIONS.CLOCK_IN_SKIP),
    staleTime: Infinity
  });
  const needsClockIn = useMemo(() => {
    return isClockInPending || getClockInError ? undefined : !timeEntry || !!timeEntry.endTime;
  }, [timeEntry, isClockInPending, getClockInError]);
  const currentShift = useMemo(() => {
    return todayShifts?.find(shift => shift.attendees.some(attendee => attendee.user.id === currentUser?.id) && toMs(shift.start) <= currentTime.getTime() && toMs(shift.end) >= currentTime.getTime());
  }, [todayShifts, currentUser?.id]);
  const withinShift = useMemo(() => {
    if (!currentShift) return false;
    return currentTime.getTime() >= toMs(currentShift.start) && currentTime.getTime() <= toMs(currentShift.end);
  }, [currentTime, currentShift]);
  const shouldAlertClockIn = useMemo(() => {
    return withinShift && needsClockIn;
  }, [withinShift, needsClockIn]);
  const shouldAlertClockOut = useMemo(() => {
    return todayShifts && todayShifts.length > 0 && !withinShift && !needsClockIn;
  }, [withinShift, needsClockIn, todayShifts]);
  const longClockInDurationMs = useMemo(() => {
    return needsClockIn === false ? getClockedInDurationMs(timeEntry?.startTime, currentTime) : null;
  }, [needsClockIn, timeEntry?.startTime, currentTime]);
  const {
    mutate: clockIn
  } = useClockInMutation();
  const {
    mutate: clockOut
  } = useClockOutMutation();
  const visible = hasClockInModule && !isClockInPending && !getClockInError && !hasPermission(currentUser, PERMISSIONS.CLOCK_IN_SKIP);
  return {
    visible,
    needsClockIn,
    shouldAlertClockIn,
    shouldAlertClockOut,
    longClockInDurationMs,
    clockIn,
    clockOut,
    locations,
    locationsLoading: isLocationsLoading,
    currentUser,
    timeEntry,
    todayShifts
  };
}